import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Sun,
  Moon,
  HelpCircle,
  ChevronDown,
  ChevronUp,
  MessageSquare,
  Calculator,
  Activity,
  UserCog,
  Settings,
  LogOut,
  UserCircle,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { toast } from 'sonner';

const FAQ_DATA = [
  {
    id: 'chat',
    category: 'Chat Clinique IA',
    icon: MessageSquare,
    iconColor: 'text-teal-600 dark:text-teal-400',
    iconBg: 'bg-teal-500/10 dark:bg-teal-500/20',
    questions: [
      {
        id: 'chat-fonctionnement',
        question: 'Comment fonctionne le chat clinique ?',
        answer: (
          <p>
            Le chat clinique utilise un modèle de langage médical spécialisé entraîné sur des
            données cliniques. Posez votre question en langage naturel (posologie, interaction,
            protocole, score…) et l'IA génère une réponse structurée adaptée au contexte
            infirmier. Chaque conversation est indépendante — commencez une nouvelle session
            pour chaque sujet.
          </p>
        ),
      },
      {
        id: 'chat-avis-medical',
        question: 'Les réponses remplacent-elles un avis médical ?',
        answer: (
          <div className="space-y-2">
            <p className="font-semibold text-red-500 dark:text-red-400">
              Non — jamais.
            </p>
            <p>
              Remadia Pro est un <strong>outil d'aide à la décision</strong>, pas un substitut
              au jugement clinique ou à la prescription médicale. Les réponses fournissent des
              informations générales de référence. En cas de doute sur une situation patient,
              référez-vous toujours au médecin prescripteur ou au protocole de service.
            </p>
          </div>
        ),
      },
      {
        id: 'chat-confidentialite',
        question: 'Mes conversations sont-elles confidentielles ?',
        answer: (
          <p>
            Oui. Vos conversations sont chiffrées avec AES-256-GCM et stockées sous votre clé
            personnelle unique. Elles ne sont jamais partagées avec des tiers. Conformité RGPD
            garantie — vos données sont hébergées en Europe. Vous pouvez supprimer votre
            historique à tout moment depuis votre compte.
          </p>
        ),
      },
    ],
  },
  {
    id: 'calculateurs',
    category: 'Calculateurs',
    icon: Calculator,
    iconColor: 'text-blue-600 dark:text-blue-400',
    iconBg: 'bg-blue-500/10 dark:bg-blue-500/20',
    questions: [
      {
        id: 'calc-dose',
        question: 'Comment utiliser le calculateur de dose ?',
        answer: (
          <div className="space-y-2">
            <p>Rendez-vous dans <strong>Calculateurs → Dose médicamenteuse</strong>. Trois modes :</p>
            <ul className="list-disc pl-5 space-y-1">
              <li><strong>Dose fixe</strong> : saisissez la dose prescrite et la concentration disponible</li>
              <li><strong>Dose au poids</strong> : dose en mg/kg × poids patient → volume à prélever</li>
              <li><strong>Reconstitution</strong> : calcul du volume de solvant pour obtenir la concentration cible</li>
            </ul>
            <p>Le chat IA intégré est disponible pour toute question sur les marges thérapeutiques ou les ajustements posologiques.</p>
          </div>
        ),
      },
      {
        id: 'calc-pse',
        question: 'Comment calculer un débit PSE ?',
        answer: (
          <div className="space-y-2">
            <p>Dans <strong>Calculateurs → PSE</strong>, renseignez :</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Le médicament et sa concentration</li>
              <li>Le poids du patient (pour les doses en µg/kg/min)</li>
              <li>La dose prescrite</li>
            </ul>
            <p>
              Le calculateur affiche le débit en ml/h directement applicable sur votre pousse-seringue. Des recettes préconfigurées sont disponibles pour les molécules courantes (dopamine, noradrénaline, insuline, morphine…).
            </p>
          </div>
        ),
      },
    ],
  },
  {
    id: 'scores',
    category: 'Scores Cliniques',
    icon: Activity,
    iconColor: 'text-indigo-600 dark:text-indigo-400',
    iconBg: 'bg-indigo-500/10 dark:bg-indigo-500/20',
    questions: [
      {
        id: 'scores-disponibles',
        question: 'Quels scores sont disponibles ?',
        answer: (
          <div className="space-y-2">
            <p>La section <strong>Scores Cliniques</strong> propose notamment :</p>
            <ul className="list-disc pl-5 space-y-1">
              <li><strong>Glasgow</strong> — évaluation de la conscience</li>
              <li><strong>Braden</strong> — risque d'escarre</li>
              <li><strong>EVA / EN</strong> — évaluation de la douleur</li>
              <li><strong>NEWS2</strong> — alerte précoce sepsis</li>
              <li><strong>NIHSS</strong> — déficit neurologique AVC</li>
              <li><strong>Apgar</strong> — vitalité néonatale</li>
            </ul>
            <p>Chaque score affiche l'interprétation clinique et les seuils d'alerte en temps réel.</p>
          </div>
        ),
      },
    ],
  },
  {
    id: 'compte',
    category: 'Compte & Abonnement',
    icon: UserCog,
    iconColor: 'text-purple-600 dark:text-purple-400',
    iconBg: 'bg-purple-500/10 dark:bg-purple-500/20',
    questions: [
      {
        id: 'compte-mdp',
        question: 'Comment modifier mon mot de passe ?',
        answer: (navigate) => (
          <p>
            Rendez-vous dans{' '}
            <button
              onClick={() => navigate('/pro/infirmier/compte')}
              className="text-teal-600 dark:text-teal-400 underline underline-offset-2 hover:text-teal-700 dark:hover:text-teal-300 font-medium"
            >
              Gestion du compte
            </button>{' '}
            → section <strong>Sécurité</strong>. Saisissez votre mot de passe actuel, le nouveau
            et sa confirmation. La mise à jour est immédiate après ré-authentification.
          </p>
        ),
      },
      {
        id: 'compte-suppression',
        question: 'Comment supprimer mon compte ?',
        answer: (navigate) => (
          <div className="space-y-2">
            <p>
              Rendez-vous dans{' '}
              <button
                onClick={() => navigate('/pro/infirmier/compte')}
                className="text-teal-600 dark:text-teal-400 underline underline-offset-2 hover:text-teal-700 dark:hover:text-teal-300 font-medium"
              >
                Gestion du compte
              </button>{' '}
              → section <strong>Compte</strong> → bouton <strong>Supprimer mon compte</strong>.
            </p>
            <p className="text-red-500 dark:text-red-400 font-medium">
              ⚠ Cette action est irréversible.
            </p>
            <p>
              Toutes vos données (conversations, analyses, profil) sont définitivement supprimées.
              Une double confirmation par mot de passe est requise.
            </p>
          </div>
        ),
      },
    ],
  },
];

const FaqProPage = () => {
  const { logout } = useAuth();
  const { user } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const isDark = theme === 'dark';

  const [openId, setOpenId] = useState(null);

  const initials =
    user?.first_name && user?.last_name
      ? `${user.first_name[0]}${user.last_name[0]}`.toUpperCase()
      : 'JD';
  const prefix = user?.profile_type === 'medecin' ? 'Dr.' : 'IDE.';
  const displayName = `${prefix} ${user?.last_name || ''}`;

  const toggle = (id) => setOpenId((prev) => (prev === id ? null : id));

  return (
    <div className="min-h-screen bg-[#F8FAFC] dark:bg-slate-950 transition-colors duration-200">

      {/* Header */}
      <header className="sticky top-0 z-50 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700 shadow-sm transition-colors duration-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/pro/infirmier')}
              className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors duration-200 mr-1"
              aria-label="Retour"
            >
              <ArrowLeft className="w-5 h-5 text-slate-600 dark:text-slate-300" />
            </button>
            <img
              src="/RemaH.png"
              alt="Remadia Pro"
              className="w-10 h-10"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
            <span className="text-xl font-bold text-[#003D82] dark:text-blue-400">Remadia</span>
            <span className="px-2 py-1 bg-teal-500/10 text-teal-600 dark:text-teal-400 text-xs font-semibold rounded-full border border-teal-500/20 dark:border-teal-500/30">
              PRO
            </span>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={toggleTheme}
              className="p-2.5 rounded-lg bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 border border-slate-200 dark:border-slate-700 transition-colors duration-200"
              aria-label="Changer de thème"
            >
              {isDark ? (
                <Sun className="w-5 h-5 text-yellow-400" />
              ) : (
                <Moon className="w-5 h-5 text-slate-600" />
              )}
            </button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 px-3 py-2 hover:bg-slate-50 dark:hover:bg-slate-800 rounded-lg transition-colors duration-200">
                  <div className="w-8 h-8 rounded-full bg-[#003D82] dark:bg-blue-600 flex items-center justify-center text-white text-sm font-semibold">
                    {initials}
                  </div>
                  <span className="hidden sm:block text-sm font-medium text-slate-700 dark:text-slate-200">
                    {displayName}
                  </span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48 dark:bg-slate-800 dark:border-slate-700 dark:text-slate-100">
                <DropdownMenuItem
                  onClick={() => navigate('/pro/infirmier/profil')}
                  className="dark:hover:bg-slate-700"
                >
                  <UserCircle className="w-4 h-4 mr-2" />
                  Mon profil
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => navigate('/pro/infirmier/compte')}
                  className="dark:hover:bg-slate-700"
                >
                  <Settings className="w-4 h-4 mr-2" />
                  Gestion du compte
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => navigate('/pro/infirmier/faq')}
                  className="dark:hover:bg-slate-700"
                >
                  <HelpCircle className="w-4 h-4 mr-2" />
                  Aide / FAQ
                </DropdownMenuItem>
                <DropdownMenuSeparator className="dark:bg-slate-700" />
                <DropdownMenuItem
                  onClick={async () => {
                    await logout();
                    toast.success('Déconnexion réussie');
                    navigate('/auth/pro');
                  }}
                  className="text-destructive dark:text-red-400 dark:hover:bg-slate-700"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Déconnexion
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="bg-gradient-to-r from-[#003D82] to-[#0056B3] dark:from-slate-800 dark:to-teal-900 text-white py-10 transition-colors duration-200">
        <div className="max-w-7xl mx-auto px-6 flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center flex-shrink-0">
            <HelpCircle className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold mb-1">Aide / FAQ</h1>
            <p className="text-blue-100 dark:text-blue-200 text-sm">
              Retrouvez les réponses aux questions fréquentes sur Remadia Pro
            </p>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <div className="max-w-3xl mx-auto px-6 py-8 space-y-6">
        {FAQ_DATA.map((cat) => {
          const CatIcon = cat.icon;
          return (
            <div
              key={cat.id}
              className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 overflow-hidden transition-colors duration-200"
            >
              {/* Category header */}
              <div className="flex items-center gap-3 px-6 py-4 border-b border-slate-100 dark:border-slate-700">
                <div className={`w-9 h-9 rounded-xl ${cat.iconBg} flex items-center justify-center flex-shrink-0`}>
                  <CatIcon className={`w-4 h-4 ${cat.iconColor}`} />
                </div>
                <h2 className="text-base font-bold text-slate-800 dark:text-slate-100">
                  {cat.category}
                </h2>
              </div>

              {/* Questions */}
              <div className="divide-y divide-slate-100 dark:divide-slate-700">
                {cat.questions.map((q) => {
                  const isOpen = openId === q.id;
                  const answer = typeof q.answer === 'function' ? q.answer(navigate) : q.answer;
                  return (
                    <div key={q.id}>
                      <button
                        onClick={() => toggle(q.id)}
                        className="w-full flex items-center justify-between px-6 py-4 text-left hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors duration-150"
                      >
                        <span className="text-sm font-semibold text-slate-700 dark:text-slate-200 pr-4">
                          {q.question}
                        </span>
                        {isOpen ? (
                          <ChevronUp className="w-4 h-4 text-slate-400 dark:text-slate-500 flex-shrink-0" />
                        ) : (
                          <ChevronDown className="w-4 h-4 text-slate-400 dark:text-slate-500 flex-shrink-0" />
                        )}
                      </button>
                      {isOpen && (
                        <div className="px-6 pb-5 text-sm text-slate-600 dark:text-slate-300 leading-relaxed border-t border-slate-100 dark:border-slate-700 pt-4">
                          {answer}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default FaqProPage;
